import React, {
  useContext,
  useEffect,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Input,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  Business as BusinessIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Link as LinkIcon,
  MonetizationOn as MonetizationOnIcon,
  Person as PersonIcon,
  RecentActors as RecentActorsIcon,
  RoomOutlined as RoomOutlinedIcon,
  Today as TodayIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import {
  Currency,
  FileData,
  FileType,
  JobDescription,
  ProcessRequestLocationType,
} from 'API';
import { debounce } from 'debounce';
import AutocompleteWithSearch from 'components/forms/AutocompleteWithSearch';
import CardBase from 'components/CardBase';
import DatePicker from 'components/forms/DatePicker';
import FileUpload from 'components/FileUpload';
import IconCircle from 'components/IconCircle';
import IconRow from 'components/IconRow';
import IconRowCell from 'components/IconRowCell';
import CompanyService from 'services/company';
import CreateProcessContext from 'shared/contexts/CreateProcessContext';
import { DICTIONARY } from 'shared/constants';
import type { AppDateValue } from 'shared/datePickerTypes';
import { ROUTES } from 'shared/routes';
import StreetAddressDisplay from 'components/StreetAddressDisplay';
import { isValidStreetAddress } from 'shared/utilities';
import CurrencyInput from 'components/forms/CurrencyInput';

const panelStyle = {
  marginTop: '1rem',
  '& > .MuiPaper-root': {
    marginBottom: '1rem',
  },
};

const getJobTypeStyles = (theme: Theme) => ({
  card: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    width: '150px',
  },
  field: {
    marginTop: theme.spacing(1),
  },
  formLabel: {
    margin: `${theme.spacing(1)} 0`,
  },
  icon: {
    color: theme.palette.primary4,
  },
  margin: {
    margin: `${theme.spacing(2)} 0`,
  },
  radio: {
    alignSelf: 'center',
  },
  currency: {
    '& #salary-input': {
      height: '30px',
      border: 0,
      borderBottom: '2px solid #f17407',
      borderRadius: '0',
      fontSize: '1rem',
      fontFamily: 'Roboto',
      fontWeight: 400,
      color: '#3c3c41',
      width: '100%',

      '&:focus-visible': {
        outline: 'none',
        borderBottom: '2px solid #f17407',
      },
    },
  },
});

interface Props {
  hideCompany?: boolean;
}

const JobType: React.FC<Props> = ({ hideCompany }: Props) => {
  const theme = useTheme();
  const styles = getJobTypeStyles(theme);
  const {
    process,
    selectedCompany,
    selectedJob,
    selectedNational,
    setProcess,
    setSelectedCompany,
    setSelectedJob,
    isExistingJob,
    setIsExistingJob,
    titleIsTaken,
    setTitleIsTaken,
    setUploadedDescription,
  } = useContext(CreateProcessContext);
  const [jobs, setJobs] = useState<Array<JobDescription>>([]);
  const company = selectedNational ? selectedNational.company : selectedCompany;

  const handleIsExistingJob = (event: any) => {
    const isExisting = event.target.value === 'true';
    setIsExistingJob(isExisting);
    if (!isExisting) {
      setProcess((draft) => {
        draft.jobDescriptionID = null;
      });
      setSelectedJob(undefined);
    }
  };

  const handleAddPremiumProcessingChange = (event: any) => {
    setProcess((draft) => {
      draft.employmentData!.premiumProcessing = event.target.value === 'true';
    });
  };

  const handleCompanySelect = (event: any, value: any) => {
    setProcess((draft) => {
      draft.companyID = value?.id;
    });
    setSelectedCompany(value);
  };

  const handleExistingJobSelection = (event: any) => {
    setProcess((draft) => {
      draft.jobDescriptionID = event.target.value || null;
    });
    setSelectedJob(
      jobs.find((job) => job?.id === event.target.value) || undefined,
    );
  };

  const handleJobTitleCustomChange = (event: any) => {
    setSelectedJob(
      {
        ...(selectedJob || {}),
        title: event.target.value,
      } as JobDescription,
    );
  };

  const handleJobURLChange = (event: any) => {
    setSelectedJob(
      {
        ...(selectedJob || {}),
        jobURL: event.target.value,
      } as JobDescription,
    );
  };

  const handleNotesChange = (event: any) => {
    setProcess((draft) => {
      draft.employmentData!.notes = event.target.value;
    });
  };

  const handleSalaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const amount = parseFloat(e.target.value.replace(/[^0-9.]/g, ''));
    const salary = {
      currency: Currency.USD,
      amount,
    };

    setProcess((draft) => {
      draft.employmentData!.salary = salary;
    });
  };

  const handleStartDateChange = (
    d: AppDateValue,
    value?: string | null | undefined,
  ) => {
    setProcess((draft) => {
      draft.employmentData!.startDate = value
        ? new Date(value).toISOString().split('T')[0]
        : value;
    });
  };

  const handleWorkingAddressChange = (event: any) => {
    setProcess((draft) => {
      draft.employmentData!.location = { type: event.target.value };
    });
  };

  const handleUploadedJobDescriptionSave = (file: FileData) => {
    setSelectedJob(
      {
        ...(selectedJob || {}),
        uploadedDescriptionID: file.id,
      } as JobDescription,
    );
    setUploadedDescription(file);
  };

  const handleUploadedJobDescriptionCancel = (file: FileData) => {
    setSelectedJob(
      {
        ...(selectedJob || {}),
        uploadedDescriptionID: file.id,
      } as JobDescription,
    );
    setUploadedDescription(undefined);
  };

  useEffect(() => {
    let ignore = false;
    const compID = company?.id;

    const load = async () => {
      const jobsList = await CompanyService.getJobDescriptions(compID!);
      if (!ignore) {
        setJobs(jobsList);
      }
    };

    if (compID) {
      load();
    } else {
      setJobs([]);
    }

    return () => { ignore = true; };
  }, [process.companyID, selectedNational?.companyID]);

  const checkJobTitleIsTaken = (companyID: string, title: string): boolean => {
    if (jobs?.length > 0) {
      return jobs.some((job) => job.companyID === companyID && job.title === title.trim());
    }

    return false;
  };

  useEffect(() => {
    let ignore = false;

    const checkJobTitle = debounce(async () => {
      const isTaken = checkJobTitleIsTaken(
        company!.id!, selectedJob!.title!,
      );

      if (!ignore) {
        setTitleIsTaken(isTaken);
      }
    }, 200);

    if (!isExistingJob && company?.id && selectedJob?.title) {
      checkJobTitle();
    }

    return () => { ignore = true; };
  }, [selectedJob?.title]);

  return (
    <Box sx={panelStyle}>
      <IconRow>
        <IconRowCell type="icon">
          <IconCircle Icon={RecentActorsIcon} />
        </IconRowCell>
        <IconRowCell type="text">
          <Typography variant="h6">{DICTIONARY.JobDetails}</Typography>
        </IconRowCell>
      </IconRow>

      <Box sx={{ mb: 4 }}>
        <RadioGroup
          aria-labelledby="existing-job-type"
          name="existing-job-type"
          value={String(isExistingJob)}
          onChange={handleIsExistingJob}
        >
          <Grid container justifyContent="space-around">
            <Grid>
              <CardBase sx={styles.card}>
                <BusinessIcon />
                <FormControlLabel
                  sx={styles.field}
                  value="true"
                  control={
                    <Radio color="primary" sx={styles.radio} />
                  }
                  labelPlacement="top"
                  label="EXISTING JOB"
                />
              </CardBase>
            </Grid>

            <Grid>
              <CardBase sx={styles.card}>
                <PersonIcon />
                <FormControlLabel
                  sx={styles.field}
                  value="false"
                  control={
                    <Radio color="primary" sx={styles.radio} />
                  }
                  labelPlacement="top"
                  label="CREATE NEW JOB"
                />
              </CardBase>
            </Grid>
          </Grid>
        </RadioGroup>
      </Box>

      {isExistingJob !== undefined && (
        <>
          {!hideCompany && (
            <IconRow sx={styles.margin} isInput>
              <IconRowCell type="icon">
                <BusinessIcon sx={styles.icon} />
              </IconRowCell>
              <IconRowCell type="text">
                <AutocompleteWithSearch
                  label={DICTIONARY.LegalNameOfUSCompany}
                  onChange={handleCompanySelect}
                  type="Company"
                  value={selectedCompany}
                />
              </IconRowCell>
            </IconRow>
          )}

          <IconRow sx={styles.margin} isInput>
            <IconRowCell type="icon">
              <MonetizationOnIcon sx={styles.icon} />
            </IconRowCell>
            <IconRowCell type="text">
              <InputLabel htmlFor="salary-input" required>Offered Salary</InputLabel>
              <Box sx={styles.currency}>
                <CurrencyInput id="salary-input" placeholder="$0.00" type="text" onChange={handleSalaryChange} />
              </Box>
            </IconRowCell>
          </IconRow>

          {isExistingJob && (
            <IconRow sx={styles.margin} isInput>
              <IconRowCell type="icon">
                <WorkIcon sx={styles.icon} />
              </IconRowCell>
              <IconRowCell type="text">
                <FormControl
                  style={{ width: '100%' }}
                >

                  {jobs.length === 0 ? (
                    <>
                      <InputLabel required shrink>
                        Job Title, Description
                      </InputLabel>
                      <Box sx={{ mt: 4 }}>
                        <Typography>
                          No jobs have been added to this company yet.
                          {company && (
                            <>
                              To add a reuseable job description, go to
                              {' '}
                              <Link to={`${ROUTES.companies}/${company.slug}`}>this company’s profile</Link>
                              .
                            </>
                          )}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <InputLabel htmlFor="job-select" required>
                        Job Title, Description
                      </InputLabel>
                      <Select
                        id="job-select"
                        onClick={handleExistingJobSelection}
                        required
                        value={process.jobDescriptionID || ''}
                      >
                        {jobs.map((job) => (
                          <MenuItem key={job!.title} value={job!.id}>
                            {job!.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  )}
                </FormControl>
              </IconRowCell>
            </IconRow>
          )}

          {isExistingJob === false && (
            <>
              <IconRow sx={styles.margin} isInput>
                <IconRowCell type="icon">
                  <WorkIcon sx={styles.icon} />
                </IconRowCell>
                <IconRowCell type="text">
                  <FormControl                    style={{ width: '100%' }}
                  >
                    <InputLabel htmlFor="job-input">Job Title *</InputLabel>
                    <Input
                      id="job-input"
                      value={selectedJob?.title || ''}
                      onChange={handleJobTitleCustomChange}
                    />
                    {titleIsTaken && (
                      <FormHelperText error>
                        {DICTIONARY.JobTitleTaken}
                      </FormHelperText>
                    )}
                  </FormControl>
                </IconRowCell>
              </IconRow>
            </>
          )}

          {isExistingJob === false && (
            <IconRow sx={styles.margin} isInput>
              <IconRowCell type="icon">
                <LinkIcon sx={styles.icon} />
              </IconRowCell>
              <IconRowCell type="text">
                <FormControl
                  style={{ width: '100%' }}
                >
                  <InputLabel htmlFor="job-url-input">
                    Job Description URL
                  </InputLabel>
                  <Input
                    id="job-url-input"
                    type="url"
                    value={selectedJob?.jobURL || ''}
                    onChange={handleJobURLChange}
                  />
                </FormControl>
              </IconRowCell>
            </IconRow>
          )}

          {isExistingJob === false && (
            <FileUpload
              label="Upload a Job Description"
              onSave={handleUploadedJobDescriptionSave}
              onCancel={handleUploadedJobDescriptionCancel}
              ownership={{
                companyID: process?.companyID || undefined,
                foreignNationalID: process?.foreignNationalID
                  || process?.employeeData?.tempID
                  || undefined,
              }}
              fileType={FileType.JOB_DESCRIPTION}
              storage={{
                ownerNationalID: process.foreignNationalID
                  || process.employeeData?.tempID
                  || '',
              }}
            />
          )}

          <IconRow sx={styles.margin} isInput>
            <IconRowCell type="icon">
              <TodayIcon sx={styles.icon} />
            </IconRowCell>
            <IconRowCell type="text">
              <FormControl                style={{ width: '100%' }}
              >
                <DatePicker
                  label="Individual’s Start Date *"
                  value={process.employmentData?.startDate || null}
                  onChange={handleStartDateChange}
                />
              </FormControl>
            </IconRowCell>
          </IconRow>

          <IconRow sx={{ mb: 4 }}>
            <IconRowCell type="icon">
              <IconCircle Icon={RoomOutlinedIcon} />
            </IconRowCell>
            <IconRowCell type="text">
              <Typography variant="h6">
                Employee’s Physical Working Address *
              </Typography>
            </IconRowCell>
          </IconRow>
          <IconRow>
            <IconRowCell type="text">
              <Typography variant="body1">
                Where is this person physically working from?
              </Typography>
              <RadioGroup
                value={process?.employmentData?.location?.type || null}
                onChange={handleWorkingAddressChange}
              >
                {isValidStreetAddress(company?.data?.addressPrimary) && (
                  <FormControlLabel
                    sx={styles.formLabel}
                    value={ProcessRequestLocationType.HEADQUARTERS}
                    control={<Radio color="primary" />}
                    label={(
                      <StreetAddressDisplay
                        address={company?.data?.addressPrimary || undefined}
                      />
                    )}
                  />
                )}
                {isValidStreetAddress(company?.data?.addressSecondary) && (
                  <FormControlLabel
                    sx={styles.formLabel}
                    value={ProcessRequestLocationType.SECONDARY}
                    control={<Radio color="primary" />}
                    label={(
                      <StreetAddressDisplay
                        address={company?.data?.addressSecondary || undefined}
                      />
                    )}
                  />
                )}
                <FormControlLabel
                  sx={styles.formLabel}
                  value={ProcessRequestLocationType.REMOTE}
                  control={<Radio color="primary" />}
                  label={<Typography>Working Remotely</Typography>}
                />
              </RadioGroup>
            </IconRowCell>
          </IconRow>

          {!company?.data?.utilizePremiumProcessingService && (
            <>
              <IconRow>
                <IconRowCell type="icon">
                  <IconCircle Icon={HistoryIcon} />
                </IconRowCell>
                <IconRowCell type="text">
                  <Typography variant="h6">
                    Would you like to add Premium Processing for an additional
                    fee? *
                  </Typography>
                </IconRowCell>
              </IconRow>

              <IconRow>
                <IconRowCell type="text">
                  <RadioGroup
                    value={String(process?.employmentData?.premiumProcessing)}
                    onChange={handleAddPremiumProcessingChange}
                  >
                    <FormControlLabel
                      sx={styles.formLabel}
                      value="true"
                      control={<Radio color="primary" />}
                      label="Yes"
                    />
                    <FormControlLabel
                      sx={styles.formLabel}
                      value="false"
                      control={<Radio color="primary" />}
                      label="No"
                    />
                  </RadioGroup>
                </IconRowCell>
              </IconRow>
            </>
          )}

          <CardBase>
            <IconRow>
              <IconRowCell type="icon">
                <IconCircle Icon={EditIcon} />
              </IconRowCell>
              <IconRowCell type="text">
                <Typography variant="h6">Add Special Notes</Typography>
              </IconRowCell>
            </IconRow>

            <FormControl style={{ width: '100%' }}>
              <InputLabel htmlFor="special-notes-input">Enter Notes</InputLabel>
              <Input
                id="special-notes-input"
                value={process.employmentData?.notes || ''}
                onChange={handleNotesChange}
                multiline
              />
            </FormControl>
          </CardBase>
        </>
      )}
    </Box>
  );
};

export default JobType;
